import React from 'react'
import clsx from 'clsx'

export type Team = {
    id: number
    name: string
}

interface TeamBadgesProps {
    teams: Team[]
    onRemove?: (team: Team) => void
    maxVisible?: number
    size?: 'sm' | 'md'
    variant?: 'default' | 'compact'
}

function TeamBadges({ 
    teams, 
    onRemove, 
    maxVisible = 3, 
    size = 'sm',
    variant = 'default' 
}: TeamBadgesProps) {
    const visibleTeams = teams.slice(0, maxVisible)
    const remainingCount = teams.length - maxVisible

    const sizeClasses = {
        sm: 'px-2 py-1 text-xs',
        md: 'px-2 py-1 text-sm'
    }

    const containerClasses = clsx(
        'flex flex-row flex-wrap',
        variant === 'compact' ? 'gap-1' : 'space-x-2'
    )

    const badgeClasses = clsx(
        'inline-flex items-center gap-x-0.5',
        'rounded-md font-medium',
        // Neutral chips — the design system keeps red as the only saturated
        // colour in UI chrome.
        'bg-gray-100 text-gray-800 ring-1 ring-inset ring-gray-300/40',
        sizeClasses[size],
        variant === 'compact' ? 'mb-1' : 'mt-2'
    )

    if (teams.length === 0) {
        return <span className="text-gray-400 text-sm">No teams</span>
    }

    return (
        <div className={containerClasses}>
            {visibleTeams.map((team) => (
                <span key={team.id} className={badgeClasses}>
                    {team.name}
                    {onRemove && (
                        <button
                            type="button"
                            className="group relative -mr-1 h-4 w-4 rounded-sm hover:bg-gray-500/20"
                            onClick={() => onRemove(team)}
                        >
                            <span className="sr-only">Remove {team.name}</span>
                            <svg
                                viewBox="0 0 14 14"
                                className="h-4 w-4 stroke-gray-700/50 group-hover:stroke-gray-700/75"
                            >
                                <path d="M4 4l6 6m0-6l-6 6" />
                            </svg>
                            <span className="absolute -inset-1" />
                        </button>
                    )}
                </span>
            ))}
            {remainingCount > 0 && (
                <span className={clsx(badgeClasses, 'bg-gray-100 text-gray-600 ring-gray-300/10')}>
                    +{remainingCount} more
                </span>
            )}
        </div>
    )
}

export default TeamBadges